"use client";

/**
 * AlertStepCard — compact in-stream card shown in chat when the agent
 * adds / edits / replaces a step inside an alert (session view_mode === "alert").
 *
 * The Alert Canvas on the right is the source of truth for the visual flow;
 * this card just confirms in the chat thread that a new step landed so the
 * user never sees the generic "Analysis completed" developer card for an
 * alert action.
 *
 * Matches the sleek "Refining approach" / switch_mode thickness:
 *   rounded-md, px-3 py-2, w-3.5 h-3.5 inline icon, text-[13px] font-medium.
 */

import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Plus, PenSquare, Replace } from "lucide-react";
import { getViewModeChrome } from "../view-mode-chrome";

type AlertStepAction = "add" | "edit" | "replace";

interface AlertStepCardProps {
  /** 1-based step number (alert-scope), e.g. "Added step 2". When null/undefined
   *  (typical during `add_cell` loading where the index isn't known yet), the
   *  number is omitted and the card reads "Adding step…" / "Added step". */
  stepNumber?: number | null;
  /** Short human purpose the agent set when creating the cell. */
  purpose?: string;
  /** true = still running, false = landed. */
  loading: boolean;
  /** Which tool triggered this card. Drives copy + corner icon. */
  action?: AlertStepAction;
  /** "signal" renders the emerald variant; anything else is icy blue. */
  signal?: boolean;
}

const VERBS: Record<
  AlertStepAction,
  { loading: string; done: string; Icon: typeof Plus; title: string }
> = {
  add: {
    loading: "Adding step",
    done: "Added step",
    Icon: Plus,
    title: "Added to alert",
  },
  edit: {
    loading: "Editing step",
    done: "Edited step",
    Icon: PenSquare,
    title: "Edited in alert",
  },
  replace: {
    loading: "Replacing step",
    done: "Replaced step",
    Icon: Replace,
    title: "Replaced in alert",
  },
};

export function AlertStepCard({
  stepNumber,
  purpose,
  loading,
  action = "add",
  signal = false,
}: AlertStepCardProps) {
  const chrome = getViewModeChrome(signal ? "signal" : "alert");
  const doneIcon = signal ? "text-emerald-300" : "text-sky-300";
  const badgeBg = signal ? "bg-emerald-300/[0.08]" : "bg-sky-300/[0.08]";
  const verb = VERBS[action];
  const StatusIcon = loading ? Loader2 : CheckCircle2;
  const hasStepNumber = typeof stepNumber === "number" && stepNumber > 0;
  const stepText = loading
    ? hasStepNumber
      ? `${verb.loading} ${stepNumber}…`
      : `${verb.loading}…`
    : hasStepNumber
      ? `${verb.done} ${stepNumber}`
      : verb.done;
  const safePurpose = (purpose || "").trim();
  const ActionIcon = verb.Icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        <div className="px-3 py-2 flex items-center gap-2.5 min-w-0">
          <StatusIcon
            className={`w-3.5 h-3.5 shrink-0 ${
              loading ? `${chrome.iconMuted} animate-spin` : doneIcon
            }`}
            strokeWidth={2.2}
          />
          <span className={`text-[13px] font-medium ${chrome.text} shrink-0 tabular-nums`}>
            {stepText}
          </span>
          {safePurpose && (
            <>
              <span className={`${chrome.subtext} opacity-50 shrink-0`}>—</span>
              <span className={`text-[13px] ${chrome.subtext} truncate min-w-0`}>
                {safePurpose}
              </span>
            </>
          )}
          <span className="flex-1" />
          <span
            aria-hidden
            className={`shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-md border ${badgeBg} ${chrome.divider} ${chrome.iconMuted}`}
            title={verb.title}
          >
            <ActionIcon className="w-3 h-3" strokeWidth={2.4} />
          </span>
        </div>
      </div>
    </motion.div>
  );
}
